import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, Clock, Trash2 } from 'lucide-react';
import type { AvailabilityRule, DateSpecificAvailability, PTODay } from '../hooks/useAvailability';

interface DayEditorSheetProps {
  date: Date;
  standardRule: AvailabilityRule | null;
  currentOverride: DateSpecificAvailability | null;
  currentPto: PTODay | null;
  onSaveCustomHours: (data: { startTime: string; endTime: string }) => void;
  onClearOverride: () => void;
  onSetPto: () => void;
  onClearPto: () => void;
  onClose: () => void;
  isSaving?: boolean;
}

export function DayEditorSheet({
  date,
  standardRule,
  currentOverride,
  currentPto,
  onSaveCustomHours,
  onClearOverride,
  onSetPto,
  onClearPto,
  onClose,
  isSaving = false,
}: DayEditorSheetProps) {
  const defaultStart = currentOverride?.startTime ?? standardRule?.startTime ?? '08:00';
  const defaultEnd = currentOverride?.endTime ?? standardRule?.endTime ?? '17:00';

  const [startTime, setStartTime] = useState(defaultStart.substring(0, 5));
  const [endTime, setEndTime] = useState(defaultEnd.substring(0, 5));

  // Reset form when selected date or effective hours change
  const effectiveStart = currentOverride?.startTime ?? standardRule?.startTime ?? '08:00';
  const effectiveEnd = currentOverride?.endTime ?? standardRule?.endTime ?? '17:00';
  useEffect(() => {
    setStartTime(effectiveStart.substring(0, 5));
    setEndTime(effectiveEnd.substring(0, 5));
  }, [date, effectiveStart, effectiveEnd]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const hasPto = currentPto !== null;
  const hasOverride = currentOverride !== null;
  const hasStandardHours = standardRule !== null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl safe-area-bottom animate-slide-up">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200">
          <span className="font-semibold text-gray-900">
            {format(date, 'EEEE, MMM d')}
          </span>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current standard hours reference */}
          <div className="text-sm">
            <span className="text-gray-500">Standard hours: </span>
            <span className="font-medium text-gray-900">
              {hasStandardHours
                ? `${standardRule.startTime.substring(0, 5)} - ${standardRule.endTime.substring(0, 5)}`
                : 'None (day off)'}
            </span>
          </div>

          {hasPto ? (
            /* PTO mode */
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800">Marked as PTO</p>
                {currentPto.reason && (
                  <p className="text-xs text-amber-700 mt-1">{currentPto.reason}</p>
                )}
              </div>
              <button
                onClick={onClearPto}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Remove PTO
              </button>
            </div>
          ) : (
            /* Edit hours mode */
            <div className="space-y-4">
              {/* Time inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => onSaveCustomHours({ startTime, endTime })}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Clock className="h-4 w-4" />
                  {hasOverride ? 'Update Custom Hours' : 'Set Custom Hours'}
                </button>

                {hasOverride && (
                  <button
                    onClick={onClearOverride}
                    disabled={isSaving}
                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Use Standard Hours
                  </button>
                )}

                <button
                  onClick={onSetPto}
                  disabled={isSaving}
                  className="w-full px-4 py-3 text-sm font-medium text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
                >
                  Mark as PTO
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom padding for safe area */}
        <div className="h-4" />
      </div>
    </>
  );
}
